import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatBRL } from '@/lib/formatters';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Download, Loader2, FileText, AlertCircle } from 'lucide-react';

const MESES = [
  { value: '01', label: 'Janeiro' }, { value: '02', label: 'Fevereiro' }, { value: '03', label: 'Março' },
  { value: '04', label: 'Abril' }, { value: '05', label: 'Maio' }, { value: '06', label: 'Junho' },
  { value: '07', label: 'Julho' }, { value: '08', label: 'Agosto' }, { value: '09', label: 'Setembro' },
  { value: '10', label: 'Outubro' }, { value: '11', label: 'Novembro' }, { value: '12', label: 'Dezembro' },
];
const ANO_ATUAL = new Date().getFullYear();
const ANOS = Array.from({ length: 4 }, (_, i) => String(ANO_ATUAL - 3 + i));

const PortalContador = () => {
  const { token } = useParams();

  const [contadorNome, setContadorNome] = useState('');
  const [notasCompra, setNotasCompra] = useState([]);
  const [notasVenda, setNotasVenda] = useState([]);
  const [notasServico, setNotasServico] = useState([]);
  const [notasTomadas, setNotasTomadas] = useState([]);
  const [outrosDocumentos, setOutrosDocumentos] = useState([]);
  const [selecionadasCompra, setSelecionadasCompra] = useState([]);
  const [selecionadasVenda, setSelecionadasVenda] = useState([]);
  const [selecionadasServico, setSelecionadasServico] = useState([]);
  const [selecionadasTomada, setSelecionadasTomada] = useState([]);
  const [selecionadasOutros, setSelecionadasOutros] = useState([]);
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState('');
  const [loading, setLoading] = useState(true);
  const [baixandoCompra, setBaixandoCompra] = useState(false);
  const [baixandoVenda, setBaixandoVenda] = useState(false);
  const [baixandoServico, setBaixandoServico] = useState(false);
  const [baixandoTomada, setBaixandoTomada] = useState(false);
  const [baixandoOutros, setBaixandoOutros] = useState(false);
  const [erro, setErro] = useState(false);

  const mes = filtroMes && filtroAno ? `${filtroAno}-${filtroMes}` : '';

  useEffect(() => {
    fetchNotas();
  }, [mes]); // eslint-disable-line

  const fetchNotas = async () => {
    try {
      setLoading(true);
      const params = mes ? { mes } : {};
      const [compraRes, vendaRes, servicoRes, tomadaRes, outrosRes] = await Promise.all([
        axiosInstance.get(`/public/notas-fiscais/${token}`, { params }),
        axiosInstance.get(`/public/notas-venda/${token}`, { params }),
        axiosInstance.get(`/public/notas-servico/${token}`, { params }),
        axiosInstance.get(`/public/notas-tomadas/${token}`, { params }),
        axiosInstance.get(`/public/outros-documentos/${token}`, { params }),
      ]);
      setContadorNome(compraRes.data.contador.nome);
      setNotasCompra(compraRes.data.notas);
      setNotasVenda(vendaRes.data.notas);
      setNotasServico(servicoRes.data.notas);
      setNotasTomadas(tomadaRes.data.notas);
      setOutrosDocumentos(outrosRes.data.notas);
      setSelecionadasCompra([]);
      setSelecionadasVenda([]);
      setSelecionadasServico([]);
      setSelecionadasTomada([]);
      setSelecionadasOutros([]);
      setErro(false);
    } catch (error) {
      setErro(true);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelecionada = (lista, setLista, id) => {
    setLista((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const baixarZip = async (endpoint, ids, nomeArquivo, setBaixando) => {
    if (ids.length === 0) {
      toast.error('Nenhuma nota disponível para baixar');
      return;
    }
    try {
      setBaixando(true);
      const response = await axiosInstance.get(endpoint, {
        params: { ids: ids.join(',') },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeArquivo;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivos');
    } finally {
      setBaixando(false);
    }
  };

  const handleBaixarSelecionadasCompra = () => baixarZip(
    `/public/notas-fiscais/${token}/zip`, selecionadasCompra, 'notas-compra.zip', setBaixandoCompra,
  );
  const handleBaixarTodasCompra = () => baixarZip(
    `/public/notas-fiscais/${token}/zip`, notasCompra.map((n) => n.id), 'notas-compra.zip', setBaixandoCompra,
  );
  const handleBaixarSelecionadasVenda = () => baixarZip(
    `/public/notas-venda/${token}/zip`, selecionadasVenda, 'notas-venda.zip', setBaixandoVenda,
  );
  const handleBaixarTodasVenda = () => baixarZip(
    `/public/notas-venda/${token}/zip`, notasVenda.map((n) => n.id), 'notas-venda.zip', setBaixandoVenda,
  );
  const handleBaixarSelecionadasServico = () => baixarZip(
    `/public/notas-servico/${token}/zip`, selecionadasServico, 'notas-servico.zip', setBaixandoServico,
  );
  const handleBaixarTodasServico = () => baixarZip(
    `/public/notas-servico/${token}/zip`, notasServico.map((n) => n.id), 'notas-servico.zip', setBaixandoServico,
  );
  const handleBaixarSelecionadasTomada = () => baixarZip(
    `/public/notas-tomadas/${token}/zip`, selecionadasTomada, 'notas-tomadas.zip', setBaixandoTomada,
  );
  const handleBaixarTodasTomada = () => baixarZip(
    `/public/notas-tomadas/${token}/zip`, notasTomadas.map((n) => n.id), 'notas-tomadas.zip', setBaixandoTomada,
  );
  const handleBaixarSelecionadasOutros = () => baixarZip(
    `/public/outros-documentos/${token}/zip`, selecionadasOutros, 'outros-documentos.zip', setBaixandoOutros,
  );
  const handleBaixarTodasOutros = () => baixarZip(
    `/public/outros-documentos/${token}/zip`, outrosDocumentos.map((n) => n.id), 'outros-documentos.zip', setBaixandoOutros,
  );

  if (erro) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
          <p className="text-zinc-400">Não encontrado</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <FileText className="w-8 h-8 text-orange-400" />
            Portal do Contador
          </h1>
          <p className="text-zinc-400 mt-1">{contadorNome}</p>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex flex-wrap gap-2">
              <Select value={filtroMes} onValueChange={setFiltroMes}>
                <SelectTrigger className="w-[140px] bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {MESES.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroAno} onValueChange={setFiltroAno}>
                <SelectTrigger className="w-[110px] bg-zinc-800 border-zinc-700">
                  <SelectValue placeholder="Ano" />
                </SelectTrigger>
                <SelectContent>
                  {ANOS.map((a) => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>Notas de Compra</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBaixarSelecionadasCompra}
                disabled={baixandoCompra || selecionadasCompra.length === 0}
                className="border-zinc-700"
              >
                {baixandoCompra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar selecionadas
              </Button>
              <Button
                onClick={handleBaixarTodasCompra}
                disabled={baixandoCompra || notasCompra.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {baixandoCompra ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar todas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : notasCompra.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma nota de compra encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Fornecedor</TableHead>
                      <TableHead>Data NF</TableHead>
                      <TableHead>Valor</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notasCompra.map((nota) => (
                      <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <Checkbox
                            checked={selecionadasCompra.includes(nota.id)}
                            onCheckedChange={() => toggleSelecionada(notasCompra, setSelecionadasCompra, nota.id)}
                          />
                        </TableCell>
                        <TableCell className="capitalize">{nota.unidade}</TableCell>
                        <TableCell>{nota.fornecedor_nome}</TableCell>
                        <TableCell>{nota.data_nf}</TableCell>
                        <TableCell>{formatBRL(nota.valor)}</TableCell>
                        <TableCell>{nota.mes_referencia}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>Notas de Venda</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBaixarSelecionadasVenda}
                disabled={baixandoVenda || selecionadasVenda.length === 0}
                className="border-zinc-700"
              >
                {baixandoVenda ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar selecionadas
              </Button>
              <Button
                onClick={handleBaixarTodasVenda}
                disabled={baixandoVenda || notasVenda.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {baixandoVenda ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar todas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : notasVenda.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma nota de venda encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notasVenda.map((nota) => (
                      <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <Checkbox
                            checked={selecionadasVenda.includes(nota.id)}
                            onCheckedChange={() => toggleSelecionada(notasVenda, setSelecionadasVenda, nota.id)}
                          />
                        </TableCell>
                        <TableCell className="capitalize">{nota.unidade}</TableCell>
                        <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                        <TableCell>{nota.mes_referencia}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>NFs Prestado</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBaixarSelecionadasServico}
                disabled={baixandoServico || selecionadasServico.length === 0}
                className="border-zinc-700"
              >
                {baixandoServico ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar selecionadas
              </Button>
              <Button
                onClick={handleBaixarTodasServico}
                disabled={baixandoServico || notasServico.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {baixandoServico ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar todas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : notasServico.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma NF Prestado encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notasServico.map((nota) => (
                      <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <Checkbox
                            checked={selecionadasServico.includes(nota.id)}
                            onCheckedChange={() => toggleSelecionada(notasServico, setSelecionadasServico, nota.id)}
                          />
                        </TableCell>
                        <TableCell className="capitalize">{nota.unidade}</TableCell>
                        <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                        <TableCell>{nota.mes_referencia}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>NFs Tomado</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBaixarSelecionadasTomada}
                disabled={baixandoTomada || selecionadasTomada.length === 0}
                className="border-zinc-700"
              >
                {baixandoTomada ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar selecionadas
              </Button>
              <Button
                onClick={handleBaixarTodasTomada}
                disabled={baixandoTomada || notasTomadas.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {baixandoTomada ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar todas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : notasTomadas.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma NF Tomado encontrada</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {notasTomadas.map((nota) => (
                      <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <Checkbox
                            checked={selecionadasTomada.includes(nota.id)}
                            onCheckedChange={() => toggleSelecionada(notasTomadas, setSelecionadasTomada, nota.id)}
                          />
                        </TableCell>
                        <TableCell className="capitalize">{nota.unidade}</TableCell>
                        <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                        <TableCell>{nota.mes_referencia}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle>Outros Documentos</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={handleBaixarSelecionadasOutros}
                disabled={baixandoOutros || selecionadasOutros.length === 0}
                className="border-zinc-700"
              >
                {baixandoOutros ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar selecionadas
              </Button>
              <Button
                onClick={handleBaixarTodasOutros}
                disabled={baixandoOutros || outrosDocumentos.length === 0}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {baixandoOutros ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Baixar todas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
              </div>
            ) : outrosDocumentos.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum documento encontrado</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Empresa</TableHead>
                      <TableHead>Arquivo</TableHead>
                      <TableHead>Mês Ref.</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {outrosDocumentos.map((nota) => (
                      <TableRow key={nota.id} className="border-zinc-800 hover:bg-zinc-800/50">
                        <TableCell>
                          <Checkbox
                            checked={selecionadasOutros.includes(nota.id)}
                            onCheckedChange={() => toggleSelecionada(outrosDocumentos, setSelecionadasOutros, nota.id)}
                          />
                        </TableCell>
                        <TableCell className="capitalize">{nota.unidade}</TableCell>
                        <TableCell>{nota.arquivo_pdf_nome}</TableCell>
                        <TableCell>{nota.mes_referencia}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PortalContador;
